/** Values `commonDesignFields`' own `width` accepts. */
const COMMON_WIDTH_VALUES = new Set(["page", "full"]);
const isNumeric = (v) => (typeof v === "number" || (typeof v === "string" && v.trim() !== "")) && Number.isFinite(Number(v));
/** Section type → its renamed width field. The single source of truth for the
 *  migration, the renderers' fallback and the `@dk/theme-templates` R13 rule. */
export const LEGACY_WIDTH_FIELDS = {
    "rich-text": { to: "text_width", owns: (v) => v === "narrow" || v === "wide" },
    "main-cart": { to: "drawer_width", owns: (v) => v === "normal" || v === "wide" || v === "full" },
    "section-divider": { to: "rule_width", owns: isNumeric },
    "custom-html": { to: "content_width", owns: (v) => v === "page" || v === "narrow" },
    "json-animation": { to: "animation_width", owns: isNumeric },
};
/** A section's own width setting, tolerating a theme that predates the rename.
 *
 *  Returns `undefined` when neither key is usable, so the caller applies its own
 *  default — which is what keeps an un-migrated `width: "full"` on `rich-text`
 *  (the common vocabulary) rendering as today: a narrow text column inside a
 *  full-bleed wrapper. */
export function ownWidth(type, settings) {
    const f = LEGACY_WIDTH_FIELDS[type];
    if (!f || !settings)
        return undefined;
    const own = settings[f.to];
    if (own !== undefined && own !== "")
        return own;
    const legacy = settings.width;
    return f.owns(legacy) ? legacy : undefined;
}
/** Migrate one section instance in place. Returns true when it changed. */
function normalizeSection(section) {
    let changed = false;
    const f = LEGACY_WIDTH_FIELDS[section.type];
    const s = section.settings;
    if (f && s && f.owns(s.width)) {
        if (s[f.to] === undefined || s[f.to] === "") {
            s[f.to] = s.width;
            changed = true;
        }
        // Own-only value (`narrow`, a percentage, a pixel count): nothing else reads
        // it, so drop it. A value the common field also accepts stays put — see the
        // module comment.
        if (!COMMON_WIDTH_VALUES.has(String(s.width))) {
            delete s.width;
            changed = true;
        }
    }
    for (const child of section.children ?? [])
        changed = normalizeSection(child) || changed;
    return changed;
}
/** Every section list a theme document holds, including template variants.
 *
 *  Exported because it is the traversal every theme-document migration needs, and
 *  a second copy is a second chance to forget the product/collection template
 *  variants — which is exactly the list that is easy to miss (`pages.product` is a
 *  map of named templates, not a section array). */
export function themeSectionLists(theme) {
    const out = [];
    if (theme.layout?.header)
        out.push(theme.layout.header);
    if (theme.layout?.footer)
        out.push(theme.layout.footer);
    const p = theme.pages;
    if (!p)
        return out;
    for (const page of [p.home, p.page, p.cart])
        if (page?.sections)
            out.push(page.sections);
    for (const group of [p.product, p.collection]) {
        for (const list of Object.values(group?.templates ?? {}))
            if (list)
                out.push(list);
    }
    return out;
}
/** Move every pre-rename `width` onto its section's renamed field, in place.
 *
 *  Idempotent — safe to run on every builder load, like `ensureColorSchemes`.
 *  Returns true when the document changed, so a caller can decide whether the
 *  migration is worth persisting. */
export function normalizeSectionWidths(theme) {
    let changed = false;
    for (const list of themeSectionLists(theme)) {
        for (const section of list)
            changed = normalizeSection(section) || changed;
    }
    return changed;
}
//# sourceMappingURL=section-width.js.map